import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";
import { post } from "@rails/request.js";

// Thank-you defaults landing prompt (#12312, #14348) on the realtime order
// screen. The server renders it only when one of the agent's four required
// Lead Defaults (photo, title, phone, NPN) is blank and no live dismissal
// covers it, in one of two chromes that share this controller:
//   * the standalone page's gl_modal (_thank_you_defaults_prompt) — opened on
//     connect through Bootstrap and closed the same way;
//   * the GOAT Store order dialog's inline notice card (`inline` value,
//     _thank_you_defaults_notice) — a dialog may carry no nested modal (#13900
//     §6), so "closing" hides the card.
// The two buttons:
//   accept  → POST apply=1 (+ dismiss=1 when "Don't ask me again" is ticked,
//             + sync_thank_you_pages=1|0 from the "Also update my active
//             thank-you pages" box — ticked by default, #14345), then writes the
//             returned values into the thank-you form fields so the screen
//             reflects the new defaults without a reload.
//   decline → POST dismiss=1 only when the box is ticked; otherwise just close
//             (asked again next visit). With the `deferPhoto` value (the photo
//             is the ONLY missing field, so the button reads "I'll add my photo
//             later" and there is no box) the deferral itself dismisses — the
//             photo is a nudge, never a requirement (owner ruling 2026-09-14).
//   When nothing can be copied, the primary is a plain link to the field's box
//   on Settings → Lead Defaults (no accept target) — the browser follows it.
export default class extends Controller {
  static targets = ["dontAsk", "syncPages", "acceptBtn", "declineBtn", "error"];
  static values = { url: String, inline: Boolean, deferPhoto: Boolean };

  connect() {
    if (this.inlineValue) return;
    this.modal = Modal.getOrCreateInstance(this.element);
    this.modal.show();
  }

  disconnect() {
    this.modal?.hide();
  }

  async accept() {
    await this.submit({
      apply: "1",
      dismiss: this.dontAskChecked() ? "1" : "0",
      sync_thank_you_pages: this.syncPagesChecked() ? "1" : "0",
    });
  }

  async decline() {
    if (!this.dontAskChecked() && !this.deferPhotoValue) {
      this.close();
      return;
    }
    await this.submit({ dismiss: "1" });
  }

  // The modal chrome hides through Bootstrap; the inline card simply leaves the
  // flow (the agent declined or the defaults are now saved).
  close() {
    if (this.modal) {
      this.modal.hide();
      return;
    }
    this.element.hidden = true;
  }

  dontAskChecked() {
    return this.hasDontAskTarget && this.dontAskTarget.checked;
  }

  // A body without the box (an older render) syncs, like the server default.
  syncPagesChecked() {
    return !this.hasSyncPagesTarget || this.syncPagesTarget.checked;
  }

  async submit(body) {
    this.setBusy(true);
    try {
      const response = await post(this.urlValue, {
        body: JSON.stringify(body),
        contentType: "application/json",
        responseKind: "json",
      });
      if (!response.ok) {
        const data = await response.json.catch(() => ({}));
        this.showError(data.error || "Something went wrong. Please try again.");
        return;
      }
      const data = await response.json;
      if (data.applied) this.applyValues(data.values || {});
      this.close();
    } catch {
      this.showError("Something went wrong. Please try again.");
    } finally {
      this.setBusy(false);
    }
  }

  // Mirror the saved defaults into the thank-you step so the form shows them
  // immediately, and retire the "Save these as my profile defaults" row (the
  // values now ARE the profile). Lookups are scoped to the order screen this
  // prompt belongs to (its realtime-order root) — a dialog opened over another
  // page must never write into that page's fields (#13905 review finding 5).
  applyValues(values) {
    const root = this.orderRoot();
    Object.entries(values).forEach(([field, value]) => {
      const input = root.querySelector(`#lead_order_${field}`);
      if (!input) return;
      input.value = value ?? "";
      input.dataset.rloDefault = value ?? "";
    });
    root.querySelectorAll('[data-prefill-source="last_order"]').forEach((hint) => hint.remove());
    const saveRow = root.querySelector('[data-realtime-order-target="saveDefaultsWrap"]');
    if (saveRow) {
      saveRow.hidden = true;
      const box = saveRow.querySelector('input[type="checkbox"]');
      if (box) box.checked = false;
    }
  }

  // The realtime-order root this prompt renders inside (the page shell or the
  // GOAT Store dialog). A prompt mounted outside any root falls back to the
  // document, which is what it did before it learned to be hosted.
  orderRoot() {
    return this.element.closest('[data-controller~="realtime-order"]') || document;
  }

  setBusy(busy) {
    [this.hasAcceptBtnTarget && this.acceptBtnTarget, this.hasDeclineBtnTarget && this.declineBtnTarget]
      .filter(Boolean)
      .forEach((btn) => { btn.disabled = busy; });
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("d-none");
  }
}
